import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import './theme.css';
import { Container } from 'react-bootstrap';
import LoadingSpinner from './components/common/LoadingSpinner';

// Layout Components
import Navbar from './components/layout/Navbar';
import Sidebar from './components/layout/Sidebar';

// Auth Components
import Login from './components/auth/Login';
import Register from './components/auth/Register';

// Dashboard Components
import Dashboard from './components/dashboard/Dashboard';

// Task Components
import TaskList from './components/tasks/TaskList';
import TaskDetail from './components/tasks/TaskDetail';
import TaskForm from './components/tasks/TaskForm';
import MyTasks from './components/tasks/MyTasks';
import TrashList from './components/tasks/TrashList';

// User Components
import UserList from './components/users/UserList';
import UserForm from './components/users/UserForm';

// Department Components
import DepartmentList from './components/departments/DepartmentList';
import DepartmentForm from './components/departments/DepartmentForm';

// Analytics Components
import DepartmentAnalytics from './components/analytics/DepartmentAnalytics';
import EmployeeAnalytics from './components/analytics/EmployeeAnalytics';
import EmployeePerformance from './components/statistics/EmployeePerformance';

// Add the import for EmployeeDetails
import EmployeeDetails from './components/employees/EmployeeDetails';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div className="loading">Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Admin/Manager Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, isManager, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (!isAdmin && !isManager) {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
};

// Admin Only Route Component
const AdminOnlyRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (!isAdmin) {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <Router basename="/">
        <div className="app-container">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            
            <Route path="/" element={
              <ProtectedRoute>
                <div className="main-layout">
                  <Navbar />
                  <div className="content-container">
                    <Sidebar />
                    <div className="main-content">
                      <Dashboard />
                    </div>
                  </div>
                </div>
              </ProtectedRoute>
            } />
            
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <div className="main-layout">
                  <Navbar />
                  <div className="content-container">
                    <Sidebar />
                    <div className="main-content">
                      <Dashboard />
                    </div>
                  </div>
                </div>
              </ProtectedRoute>
            } />
            
            <Route path="/my-tasks" element={
              <AdminRoute>
                <div className="main-layout">
                  <Navbar />
                  <div className="content-container">
                    <Sidebar />
                    <div className="main-content">
                      <MyTasks />
                    </div>
                  </div>
                </div>
              </AdminRoute>
            } />
            
            <Route path="/tasks" element={
              <ProtectedRoute>
                <div className="main-layout">
                  <Navbar />
                  <div className="content-container">
                    <Sidebar />
                    <div className="main-content">
                      <TaskList />
                    </div>
                  </div>
                </div>
              </ProtectedRoute>
            } />
            
            <Route path="/tasks/:id" element={
              <ProtectedRoute>
                <div className="main-layout">
                  <Navbar />
                  <div className="content-container">
                    <Sidebar />
                    <div className="main-content">
                      <TaskDetail />
                    </div>
                  </div>
                </div>
              </ProtectedRoute>
            } />
            
            <Route path="/tasks/new" element={
              <AdminRoute>
                <div className="main-layout">
                  <Navbar />
                  <div className="content-container">
                    <Sidebar />
                    <div className="main-content">
                      <TaskForm />
                    </div>
                  </div>
                </div>
              </AdminRoute>
            } />
            
            <Route path="/users" element={
              <AdminRoute>
                <div className="main-layout">
                  <Navbar />
                  <div className="content-container">
                    <Sidebar />
                    <div className="main-content">
                      <UserList />
                    </div>
                  </div>
                </div>
              </AdminRoute>
            } />
            
            <Route path="/users/new" element={
              <AdminRoute>
                <div className="main-layout">
                  <Navbar />
                  <div className="content-container">
                    <Sidebar />
                    <div className="main-content">
                      <UserForm />
                    </div>
                  </div>
                </div>
              </AdminRoute>
            } />
            
            <Route path="/users/:id/edit" element={
              <AdminRoute>
                <div className="main-layout">
                  <Navbar />
                  <div className="content-container">
                    <Sidebar />
                    <div className="main-content">
                      <UserForm />
                    </div>
                  </div>
                </div>
              </AdminRoute>
            } />
            
            <Route path="/departments" element={
              <AdminRoute>
                <div className="main-layout">
                  <Navbar />
                  <div className="content-container">
                    <Sidebar />
                    <div className="main-content">
                      <DepartmentList />
                    </div>
                  </div>
                </div>
              </AdminRoute>
            } />
            
            <Route path="/departments/new" element={
              <AdminRoute>
                <div className="main-layout">
                  <Navbar />
                  <div className="content-container">
                    <Sidebar />
                    <div className="main-content">
                      <DepartmentForm />
                    </div>
                  </div>
                </div>
              </AdminRoute>
            } />
            
            <Route path="/departments/:id/edit" element={
              <AdminRoute>
                <div className="main-layout">
                  <Navbar />
                  <div className="content-container">
                    <Sidebar />
                    <div className="main-content">
                      <DepartmentForm />
                    </div>
                  </div>
                </div>
              </AdminRoute>
            } />
            
            <Route path="/tasks/:id/edit" element={
              <AdminRoute>
                <div className="main-layout">
                  <Navbar />
                  <div className="content-container">
                    <Sidebar />
                    <div className="main-content">
                      <TaskForm />
                    </div>
                  </div>
                </div>
              </AdminRoute>
            } />
            
            {/* Analytics Routes */}
            <Route path="/analytics/departments" element={
              <AdminRoute>
                <div className="main-layout">
                  <Navbar />
                  <div className="content-container">
                    <Sidebar />
                    <div className="main-content">
                      <DepartmentAnalytics />
                    </div>
                  </div>
                </div>
              </AdminRoute>
            } />
            
            <Route path="/analytics/employees" element={
              <AdminRoute>
                <div className="main-layout">
                  <Navbar />
                  <div className="content-container">
                    <Sidebar />
                    <div className="main-content">
                      <EmployeeAnalytics />
                    </div>
                  </div>
                </div>
              </AdminRoute>
            } />

            {/* Employee Performance Route */}
            <Route path="/my-statistics" element={
              <ProtectedRoute>
                <div className="main-layout">
                  <Navbar />
                  <div className="content-container">
                    <Sidebar />
                    <div className="main-content">
                      <EmployeePerformance />
                    </div>
                  </div>
                </div>
              </ProtectedRoute>
            } />

            {/* Employee Details Route */}
            <Route path="/employee-details" element={
              <AdminRoute>
                <div className="main-layout">
                  <Navbar />
                  <div className="content-container">
                    <Sidebar />
                    <div className="main-content">
                      <EmployeeDetails />
                    </div>
                  </div>
                </div>
              </AdminRoute>
            } />

            {/* Add Trash Route */}
            <Route path="/tasks/trash" element={
              <AdminOnlyRoute>
                <div className="main-layout">
                  <Navbar />
                  <div className="content-container">
                    <Sidebar />
                    <div className="main-content">
                      <TrashList />
                    </div>
                  </div>
                </div>
              </AdminOnlyRoute>
            } />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
